
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Menu,
    MenuItem,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    Avatar,
    Divider,
    Checkbox,
    Tooltip,
    Pagination,
    CircularProgress,
    Alert
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    MoreVert as MoreVertIcon,
    Group as GroupIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    Edit as EditIcon,
    SwapHoriz as SwapIcon,
    ViewModule as ViewModuleIcon,
    KeyboardArrowDown as ArrowDownIcon,
    ChevronLeft as ChevronLeftIcon,
    PersonAddAlt as InviteIcon,
    Refresh as RefreshIcon,
    Assignment as AssignmentIcon,
    BarChart as BarChartIcon,
    PieChart as PieChartIcon,
    TableChart as TableChartIcon,
    ListAlt as ListAltIcon,
    FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip as ChartTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import client from '../../api/client';
import { useAuth } from '../../context/AuthContext';

// Register ChartJS
ChartJS.register(
    ArcElement,
    ChartTooltip,
    Legend,
    CategoryScale,
    LinearScale,
    BarElement,
    Title
);

const OrganizationPage = () => {
    const { user } = useAuth();
    const isSuperAdmin = user?.email === 'immanueldavid@mtandt.com';

    const [activeTab, setActiveTab] = useState(0);
    // Landing default: Mtandt company → Purchasing department (asked by ops).
    const [selectedDept, setSelectedDept] = useState('Purchasing');
    const [selectedCompany, setSelectedCompany] = useState('Mtandt');
    const [users, setUsers] = useState([]);
    // Field operators (HR roster, operators table) — no accounts; read-only rows.
    const [operators, setOperators] = useState([]);
    const [operatorDetail, setOperatorDetail] = useState(null); // read-only dialog
    const [departments, setDepartments] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [deptAnchorEl, setDeptAnchorEl] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedDeptForMenu, setSelectedDeptForMenu] = useState(null);
    const [openMoveDialog, setOpenMoveDialog] = useState(false);
    const [openAddMemberDialog, setOpenAddMemberDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [targetDept, setTargetDept] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [statusAnchorEl, setStatusAnchorEl] = useState(null);
    const [memberFilter, setMemberFilter] = useState('Direct Members Only');
    const [memberFilterAnchorEl, setMemberFilterAnchorEl] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [page, setPage] = useState(1);
    const [newMember, setNewMember] = useState({
        name: '',
        email: '',
        mobile_number: '',
        gender: '',
        department: '',
        company: '',
        role: 'user',
        specialization: '',
        certification_level: 'junior',
        hourly_rate: 0,
        user_id_code: '',
        password: '',
        accessables: ''
    });
    const [editMember, setEditMember] = useState({});

    // Survey States
    const [surveyForms, setSurveyForms] = useState([]);
    const [surveyResponses, setSurveyResponses] = useState([]);
    const [selectedFormId, setSelectedFormId] = useState(null);
    const [surveyLoading, setSurveyLoading] = useState(false);
    const [surveyTabView, setSurveyTabView] = useState('list'); // 'list', 'details'
    const [surveyVisualMode, setSurveyVisualMode] = useState('table'); // 'table', 'graph'
    const [openSurveyGuide, setOpenSurveyGuide] = useState(false);

    // Fetch users
    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await client.get('/users');
            if (res.success) {
                setUsers(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch users", err);
            setError("Failed to load users. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Field operators — merged into the members list (Mtandt → Operations).
    const fetchOperators = async () => {
        try {
            const res = await client.get('/users/operators');
            if (res.success) {
                setOperators(res.data || []);
            }
        } catch (err) {
            console.error("Failed to fetch operators", err);
            setOperators([]);
        }
    };

    // Fetch departments
    const fetchDepartments = async () => {
        try {
            const res = await client.get('/users/departments');
            if (res.success) {
                setDepartments(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch departments", err);
            // Fallback: extract departments from users
            const uniqueDepts = [...new Set(users.filter(u => u.department).map(u => u.department))];
            setDepartments(uniqueDepts);
        }
    };

    // Fetch companies
    const fetchCompanies = async () => {
        try {
            const res = await client.get('/users/companies');
            if (res.success) {
                setCompanies(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch companies", err);
            // Fallback: extract companies from users
            const uniqueCompanies = [...new Set(users.filter(u => u.company).map(u => u.company))];
            setCompanies(uniqueCompanies.map(c => ({ company: c, user_count: users.filter(u => u.company === c).length })));
        }
    };

    // Survey Fetch Functions
    const fetchSurveyForms = async () => {
        try {
            setSurveyLoading(true);
            const res = await client.get('/surveys/forms');
            if (res.success) {
                setSurveyForms(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch survey forms", err);
        } finally {
            setSurveyLoading(false);
        }
    };

    const fetchSurveyResponses = async (formId) => {
        try {
            setSurveyLoading(true);
            const res = await client.get(`/surveys/responses?form_id=${formId}`);
            if (res.success) {
                setSurveyResponses(res.data);
            }
        } catch (err) {
            console.error("Failed to fetch survey responses", err);
        } finally {
            setSurveyLoading(false);
        }
    };

    const handleDownloadSurveyData = () => {
        if (surveyResponses.length === 0) return;
        
        const formName = surveyForms.find(f => f.form_id === selectedFormId)?.form_name || 'survey_responses';
        
        // Define CSV headers - Removed redundant 'Employee' and 'Email'
        const questions = Object.keys(surveyResponses[0].survey_data || {});
        const headers = ['Date', ...questions];
        
        // Create CSV rows - Removed redundant 'm_name' and 'employee_email'
        const rows = surveyResponses.map(row => [
            new Date(row.created_at).toLocaleDateString(),
            ...questions.map(q => row.survey_data[q] || '')
        ]);
        
        // Combine headers and rows with proper escaping
        const csvContent = [headers, ...rows]
            .map(e => e.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(","))
            .join("\n");
            
        // Trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${formName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        fetchUsers();
        fetchOperators();
        fetchDepartments();
        fetchCompanies();
    }, []);

    // Re-fetch departments when users change
    useEffect(() => {
        if (users.length > 0) {
            const uniqueDepts = [...new Set(users.filter(u => u.department).map(u => u.department))];
            if (departments.length === 0) {
                setDepartments(uniqueDepts);
            }
        }
    }, [users]);

    useEffect(() => {
        if (activeTab === 3) {
            fetchSurveyForms();
            setSurveyTabView('list');
        }
    }, [activeTab]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleMenuOpen = (event, user) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedUser(null);
    };

    const handleDeptMenuOpen = (event, dept) => {
        event.stopPropagation();
        setDeptAnchorEl(event.currentTarget);
        setSelectedDeptForMenu(dept);
    };

    const handleDeptMenuClose = () => {
        setDeptAnchorEl(null);
        setSelectedDeptForMenu(null);
    };

    const handleMoveClick = () => {
        setTargetDept(selectedUser?.department || '');
        setOpenMoveDialog(true);
        setAnchorEl(null);
    };

    const handleEditClick = (user = null) => {
        const userToEdit = user || selectedUser;
        if (!userToEdit) return;
        setEditMember({
            id: userToEdit.id,
            user_id_code: userToEdit.user_id_code || '',
            name: userToEdit.name || '',
            email: userToEdit.email || '',
            mobile_number: userToEdit.mobile_number || '',
            gender: userToEdit.gender || '',
            department: userToEdit.department || '',
            company: userToEdit.company || '',
            role: userToEdit.role || 'user',
            specialization: userToEdit.specialization || '',
            certification_level: userToEdit.certification_level || 'junior',
            hourly_rate: userToEdit.hourly_rate || 0,
            password: '', // Keep empty for security
            accessables: userToEdit.accessables || '',
            created_at: userToEdit.created_at || '',
            last_login: userToEdit.last_login || '',
            // Email Settings
            config_email: userToEdit.config_email || userToEdit.email || '',
            smtp_host: userToEdit.smtp_host || 'smtp.gmail.com',
            smtp_port: userToEdit.smtp_port || 587,
            smtp_user: userToEdit.smtp_user || userToEdit.config_email || userToEdit.email || '',
            smtp_pass: '', // Always start empty for security
            imap_host: userToEdit.imap_host || 'imap.gmail.com',
            imap_port: userToEdit.imap_port || 993,
            imap_user: userToEdit.imap_user || userToEdit.config_email || userToEdit.email || '',
            imap_pass: '' // Always start empty for security
        });
        setOpenEditDialog(true);
        setAnchorEl(null);
    };

    const handleDeleteClick = () => {
        setOpenDeleteDialog(true);
        setAnchorEl(null);
    };

    const handleMoveConfirm = async () => {
        if (!selectedUser) return;
        try {
            await client.put(`/users/${selectedUser.id}`, {
                department: targetDept
            });
            fetchUsers();
            fetchDepartments();
            setOpenMoveDialog(false);
            setSelectedUser(null);
        } catch (err) {
            console.error("Failed to move user", err);
            setError("Failed to change department. Please try again.");
        }
    };

    const handleEditConfirm = async () => {
        if (!editMember.id) return;
        try {
            await client.put(`/users/${editMember.id}`, editMember);
            fetchUsers();
            fetchDepartments();
            setOpenEditDialog(false);
            setEditMember({});
        } catch (err) {
            console.error("Failed to update user", err);
            setError("Failed to update user. Please try again.");
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedUser) return;
        try {
            await client.delete(`/users/${selectedUser.id}`);
            fetchUsers();
            fetchDepartments();
            setOpenDeleteDialog(false);
            setSelectedUser(null);
        } catch (err) {
            console.error("Failed to delete user", err);
            setError("Failed to delete user. Please try again.");
        }
    };

    const handleAddMember = async () => {
        try {
            if (!newMember.name || !newMember.email) {
                setError("Name and email are required");
                return;
            }
            await client.post('/users', newMember);
            fetchUsers();
            fetchDepartments();
            setOpenAddMemberDialog(false);
            setNewMember({
                name: '',
                email: '',
                mobile_number: '',
                gender: '',
                department: '',
                company: '',
                role: 'user',
                specialization: '',
                certification_level: 'junior',
                hourly_rate: 0,
                user_id_code: '',
                password: '',
                accessables: ''
            });
        } catch (err) {
            console.error("Failed to add member", err);
            setError(err.response?.data?.message || "Failed to add member. Please try again.");
        }
    };

    const handleSelectUser = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) {
            setSelectedUsers(filteredUsers.map(user => user.id));
        } else {
            setSelectedUsers([]);
        }
    };

    // Website users + field operators (read-only, no accounts) as ONE roster.
    const members = [...users, ...operators];

    const filteredUsers = members.filter(user => {
        const matchesSearch = user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.mobile_number?.includes(searchQuery) ||
            user.user_id_code?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesDept = selectedDept === 'All' || user.department === selectedDept;
        const matchesCompany = selectedCompany === 'All' || user.company === selectedCompany;
        return matchesSearch && matchesDept && matchesCompany;
    });

    // Department names across users AND operators (e.g. Operations exists even
    // if no website account carries it).
    const allDepartments = [...new Set([
        ...departments,
        ...operators.map(o => o.department).filter(Boolean),
    ])];

    // Sidebar company list with operator headcount folded in (operators all
    // belong to Mtandt; the /users/companies endpoint only counts real users).
    const sidebarCompanies = (() => {
        const list = companies.map(c => ({ ...c }));
        if (operators.length > 0) {
            const mt = list.find(c => (c.company || '').toLowerCase() === 'mtandt');
            if (mt) mt.user_count = Number(mt.user_count || 0) + operators.length;
            else list.push({ company: 'Mtandt', user_count: operators.length });
        }
        return list;
    })();

    const usersPerPage = 10;
    const paginatedUsers = filteredUsers.slice((page - 1) * usersPerPage, page * usersPerPage);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return { bg: '#fee2e2', color: '#dc2626' };
            case 'supervisor': return { bg: '#fef3c7', color: '#d97706' };
            case 'technician': return { bg: '#dbeafe', color: '#2563eb' };
            case 'operator': return { bg: '#d1fae5', color: '#059669' };
            default: return { bg: '#f3f4f6', color: '#6b7280' };
        }
    };

    return (
        <Box sx={{ pb: 5 }}>
            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                    mb: 3,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '1rem', mr: 2 }
                }}
            >
                <Tab label="Members" />
                <Tab label="Departments" />
                <Tab label="Deleted Members" />
                <Tab label="Survey Responses" />
            </Tabs>

            {activeTab === 0 && (
                <Box sx={{ display: 'flex', gap: 2, minHeight: 'calc(100vh - 300px)' }}>
                    {/* Left Sidebar */}
                    <Paper
                        sx={{
                            width: sidebarCollapsed ? 60 : 240,
                            flexShrink: 0,
                            borderRadius: '16px',
                            overflow: 'hidden',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                            transition: 'width 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Search */}
                        {!sidebarCollapsed && (
                            <Box sx={{ p: 2 }}>
                                <TextField
                                    fullWidth
                                    placeholder="Enter name, email..."
                                    size="small"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                        sx: { borderRadius: '10px', bgcolor: 'background.default', fontSize: '0.875rem' }
                                    }}
                                />
                            </Box>
                        )}

                        {/* Collapse button */}
                        <Box sx={{ display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-end', px: 1 }}>
                            <IconButton
                                size="small"
                                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                sx={{
                                    bgcolor: 'action.hover',
                                    width: 24,
                                    height: 24,
                                    transform: sidebarCollapsed ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.3s ease'
                                }}
                            >
                                <ChevronLeftIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>

                        {/* Companies List */}
                        {!sidebarCollapsed && (
                            <Box sx={{ px: 2, py: 1 }}>
                                {/* Show All Users option */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        p: 1.5,
                                        borderRadius: '10px',
                                        bgcolor: selectedCompany === 'All' && selectedDept === 'All' ? 'primary.main' : 'transparent',
                                        color: selectedCompany === 'All' && selectedDept === 'All' ? 'white' : 'text.primary',
                                        cursor: 'pointer',
                                        mb: 1,
                                        '&:hover': {
                                            bgcolor: selectedCompany === 'All' && selectedDept === 'All' ? 'primary.dark' : 'action.hover'
                                        }
                                    }}
                                    onClick={() => { setSelectedCompany('All'); setSelectedDept('All'); }}
                                >
                                    <Avatar
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: selectedCompany === 'All' && selectedDept === 'All' ? 'rgba(255,255,255,0.2)' : 'primary.main',
                                            fontSize: '0.875rem',
                                            fontWeight: 700
                                        }}
                                    >
                                        A
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            All Companies
                                        </Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                            {members.length} users
                                        </Typography>
                                    </Box>
                                </Box>

                                {/* Company list */}
                                {sidebarCompanies.map((comp) => {
                                    const isSelectedCompany = selectedCompany === comp.company;
                                    const companyDepartments = [...new Set(members.filter(u => u.company === comp.company && u.department).map(u => u.department))];

                                    return (
                                        <Box key={comp.company} sx={{ mb: 1 }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    p: 1.5,
                                                    borderRadius: '10px',
                                                    bgcolor: isSelectedCompany && selectedDept === 'All' ? 'primary.main' : 'transparent',
                                                    color: isSelectedCompany && selectedDept === 'All' ? 'white' : 'text.primary',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: isSelectedCompany && selectedDept === 'All' ? 'primary.dark' : 'action.hover'
                                                    }
                                                }}
                                                onClick={() => { setSelectedCompany(comp.company); setSelectedDept('All'); }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 32,
                                                        height: 32,
                                                        bgcolor: isSelectedCompany && selectedDept === 'All' ? 'rgba(255,255,255,0.2)' : 'primary.main',
                                                        fontSize: '0.875rem',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    {comp.company?.charAt(0) || 'C'}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {comp.company}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                        {comp.user_count} users
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Departments under this company */}
                                            {isSelectedCompany && companyDepartments.length > 0 && (
                                                <List dense sx={{ py: 0, pl: 2 }}>
                                                    {companyDepartments.map((dept) => {
                                                        const count = members.filter(u => u.company === comp.company && u.department === dept).length;
                                                        const isDeptSelected = selectedDept === dept;
                                                        return (
                                                            <ListItem
                                                                key={dept}
                                                                button
                                                                selected={isDeptSelected}
                                                                onClick={() => setSelectedDept(dept)}
                                                                sx={{
                                                                    py: 1,
                                                                    pl: 2,
                                                                    borderLeft: isDeptSelected ? '3px solid #2563eb' : '3px solid transparent',
                                                                    bgcolor: isDeptSelected ? 'rgba(37,99,235,0.05)' : 'transparent',
                                                                    '&:hover': {
                                                                        bgcolor: isDeptSelected ? 'rgba(37,99,235,0.08)' : 'action.hover'
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemIcon sx={{ minWidth: 28 }}>
                                                                    <GroupIcon color={isDeptSelected ? 'primary' : 'action'} sx={{ fontSize: 18 }} />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={dept}
                                                                    secondary={`${count} members`}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: isDeptSelected ? 600 : 400
                                                                    }}
                                                                    secondaryTypographyProps={{
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Paper>

                    {/* Right Content */}
                    <Paper sx={{
                        flex: 1,
                        borderRadius: '16px',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {/* Toolbar */}
                        <Box sx={{
                            p: 2,
                            borderBottom: '1px solid rgba(226,232,240,0.6)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    {selectedCompany === 'All' ? 'All Companies' : selectedCompany}{selectedDept !== 'All' ? ` - ${selectedDept}` : ''}
                                </Typography>
                                <Chip
                                    label={`Users ${filteredUsers.length}`}
                                    size="small"
                                    sx={{
                                        borderRadius: '6px',
                                        bgcolor: 'transparent',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        fontWeight: 500,
                                        fontSize: '0.75rem'
                                    }}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                {/* Status Filter */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    endIcon={<ArrowDownIcon />}
                                    onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        borderColor: 'divider',
                                        color: 'text.primary',
                                        px: 1.5
                                    }}
                                >
                                    Status <Typography component="span" sx={{ ml: 0.5, color: 'text.secondary' }}>{statusFilter}</Typography>
                                </Button>
                                <Menu
                                    anchorEl={statusAnchorEl}
                                    open={Boolean(statusAnchorEl)}
                                    onClose={() => setStatusAnchorEl(null)}
                                >
                                    {['All', 'Active', 'Inactive'].map(status => (
                                        <MenuItem
                                            key={status}
                                            onClick={() => { setStatusFilter(status); setStatusAnchorEl(null); }}
                                            selected={statusFilter === status}
                                        >
                                            {status}
                                        </MenuItem>
                                    ))}
                                </Menu>

                                {/* Member Filter */}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    endIcon={<ArrowDownIcon />}
                                    onClick={(e) => setMemberFilterAnchorEl(e.currentTarget)}
                                    sx={{
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        borderColor: 'divider',
                                        color: 'text.primary',
                                        px: 1.5
                                    }}
                                >
                                    {memberFilter}
                                </Button>
                                <Menu
                                    anchorEl={memberFilterAnchorEl}
                                    open={Boolean(memberFilterAnchorEl)}
                                    onClose={() => setMemberFilterAnchorEl(null)}
                                >
                                    {['Direct Members Only', 'All Members'].map(filter => (
                                        <MenuItem
                                            key={filter}
                                            onClick={() => { setMemberFilter(filter); setMemberFilterAnchorEl(null); }}
                                            selected={memberFilter === filter}
                                        >
                                            {filter}
                                        </MenuItem>
                                    ))}
                                </Menu>

                                {/* View Toggle */}
                                <IconButton size="small" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
                                    <ViewModuleIcon sx={{ fontSize: 18 }} />
                                </IconButton>

                                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                                {isSuperAdmin && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<InviteIcon sx={{ fontSize: 18 }} />}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            borderColor: 'divider',
                                            color: 'text.primary',
                                            px: 1.5
                                        }}
                                    >
                                        Invite Member
                                    </Button>
                                )}

                                {isSuperAdmin && (
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => setOpenAddMemberDialog(true)}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 2,
                                            boxShadow: 'none',
                                            '&:hover': {
                                                boxShadow: '0 2px 8px rgba(37,99,235,0.3)'
                                            }
                                        }}
                                    >
                                        Add Members
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        {/* Table */}
                        <TableContainer sx={{ flex: 1 }}>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox" sx={{ bgcolor: '#f8fafc' }}>
                                                <Checkbox
                                                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length}
                                                    checked={filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                                                    onChange={handleSelectAll}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#f8fafc' }}>Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#f8fafc' }}>Email</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#f8fafc' }}>Mobile</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#f8fafc' }}>Department</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#f8fafc' }}>Company</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: 'text.secondary', bgcolor: '#f8fafc' }}>Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedUsers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                        <Avatar sx={{ width: 64, height: 64, bgcolor: 'action.selected' }}>
                                                            <GroupIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                                                        </Avatar>
                                                        <Typography color="text.secondary">
                                                            {selectedDept === 'All'
                                                                ? 'No members found in the organization'
                                                                : `No members found in ${selectedDept} department`}
                                                        </Typography>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<PersonAddIcon />}
                                                            onClick={() => {
                                                                setNewMember(prev => ({ ...prev, department: selectedDept === 'All' ? '' : selectedDept }));
                                                                setOpenAddMemberDialog(true);
                                                            }}
                                                            sx={{ borderRadius: '8px', textTransform: 'none' }}
                                                        >
                                                            Add First Member
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedUsers.map((user) => {
                                                const roleStyle = getRoleColor(user.role);
                                                return (
                                                    <TableRow
                                                        key={user.id}
                                                        hover
                                                        selected={selectedUsers.includes(user.id)}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedUsers.includes(user.id)}
                                                                onChange={() => handleSelectUser(user.id)}
                                                                size="small"
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                                <Avatar
                                                                    sx={{
                                                                        width: 32,
                                                                        height: 32,
                                                                        bgcolor: 'primary.light',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight={500}>
                                                                        {user.name}
                                                                    </Typography>
                                                                    {user.user_id_code && (
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            {user.user_id_code}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {user.email || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {user.mobile_number || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {user.department ? (
                                                                <Chip
                                                                    label={user.department}
                                                                    size="small"
                                                                    sx={{
                                                                        borderRadius: '6px',
                                                                        fontSize: '0.75rem',
                                                                        height: 24
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.disabled">Unassigned</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {user.company ? (
                                                                <Chip
                                                                    label={user.company}
                                                                    size="small"
                                                                    sx={{
                                                                        borderRadius: '6px',
                                                                        fontSize: '0.75rem',
                                                                        height: 24,
                                                                        bgcolor: 'rgba(0,0,0,0.04)',
                                                                        color: 'text.primary',
                                                                        fontWeight: 600
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" color="text.disabled">-</Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    variant="text"
                                                                    onClick={() => user.is_operator ? setOperatorDetail(user) : handleEditClick(user)}
                                                                    sx={{
                                                                        textTransform: 'none',
                                                                        color: 'primary.main',
                                                                        fontWeight: 500,
                                                                        minWidth: 'auto',
                                                                        p: 0.5
                                                                    }}
                                                                >
                                                                    Details
                                                                </Button>
                                                                {isSuperAdmin && !user.is_operator && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => handleMenuOpen(e, user)}
                                                                        sx={{ p: 0.5 }}
                                                                    >
                                                                        <MoreVertIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            )}
                        </TableContainer>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Box sx={{
                                p: 2,
                                borderTop: '1px solid rgba(226,232,240,0.6)',
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={(e, value) => setPage(value)}
                                    size="small"
                                    shape="rounded"
                                />
                            </Box>
                        )}
                    </Paper>
                </Box>
            )}

            {/* Departments Tab */}
            {activeTab === 1 && (
                <Paper sx={{ borderRadius: '16px', p: 3, border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" fontWeight={700}>All Departments ({allDepartments.length})</Typography>
                    </Box>
                    {allDepartments.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Avatar sx={{ width: 64, height: 64, bgcolor: 'action.selected', mx: 'auto', mb: 2 }}>
                                <GroupIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                            </Avatar>
                            <Typography color="text.secondary" sx={{ mb: 2 }}>No departments found</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Departments are created when you assign users to them
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {allDepartments.map(dept => (
                                <Paper
                                    key={dept}
                                    sx={{
                                        p: 2,
                                        minWidth: 200,
                                        borderRadius: '12px',
                                        border: '1px solid rgba(226,232,240,0.8)',
                                        cursor: 'pointer',
                                        '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(37,99,235,0.02)' }
                                    }}
                                    onClick={() => { setSelectedDept(dept); setActiveTab(0); }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.light' }}>
                                            <GroupIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={600}>{dept}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {members.filter(u => u.department === dept).length} members
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Paper>
                            ))}
                        </Box>
                    )}
                </Paper>
            )}

            {/* Deleted Members Placeholder */}
            {activeTab === 2 && (
                <Paper sx={{ borderRadius: '16px', p: 8, border: '1px solid rgba(226, 232, 240, 0.8)', textAlign: 'center' }}>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: 'action.selected', mx: 'auto', mb: 2 }}>
                        <DeleteIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    </Avatar>
                    <Typography color="text.secondary">No deleted members to display</Typography>
                </Paper>
            )}

            {/* Survey Responses Tab */}
            {activeTab === 3 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {surveyLoading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
                    
                    {!surveyLoading && surveyTabView === 'list' && (
                        <Paper sx={{ borderRadius: '16px', p: 3, border: '1px solid rgba(226, 232, 240, 0.8)' }}>
                            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="h6" fontWeight={700}>Survey Forms</Typography>
                                    <Typography variant="caption" color="text.secondary">Available forms from Google integration</Typography>
                                </Box>
                                {isSuperAdmin && (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<RefreshIcon />}
                                            onClick={() => setOpenSurveyGuide(true)}
                                            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                                        >
                                            Connection Guide
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={() => window.open('https://docs.google.com/forms/u/0/create', '_blank')}
                                            sx={{ 
                                                borderRadius: '8px', 
                                                textTransform: 'none', 
                                                fontWeight: 600,
                                                boxShadow: 'none',
                                                '&:hover': { boxShadow: '0 2px 8px rgba(37,99,235,0.3)' }
                                            }}
                                        >
                                            Create Survey
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {surveyForms.length === 0 ? (
                                <Box sx={{ py: 6, textAlign: 'center' }}>
                                    <AssignmentIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                    <Typography color="text.secondary" sx={{ mb: 2 }}>No survey data yet. Connect your Google Form to push responses here.</Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => window.open('https://docs.google.com/forms/u/0/create', '_blank')}
                                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                                    >
                                        Create Your First Survey
                                    </Button>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                                    {surveyForms.map(form => (
                                        <Paper 
                                            key={form.form_id}
                                            sx={{ 
                                                p: 3, 
                                                borderRadius: '12px', 
                                                border: '1px solid rgba(226,232,240,0.8)',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderColor: 'primary.main' }
                                            }}
                                            onClick={() => {
                                                setSelectedFormId(form.form_id);
                                                fetchSurveyResponses(form.form_id);
                                                setSurveyTabView('details');
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                                                <Avatar sx={{ bgcolor: 'secondary.light', width: 48, height: 48 }}>
                                                    <AssignmentIcon />
                                                </Avatar>
                                                <Chip label={`${form.response_count} Responses`} size="small" color="primary" variant="outlined" />
                                            </Box>
                                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>{form.form_name}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Form ID: {form.form_id.substring(0, 10)}...</Typography>
                                            <Typography variant="caption" color="text.secondary">Last response: {new Date(form.last_response).toLocaleString()}</Typography>
                                        </Paper>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    )}

                    {!surveyLoading && surveyTabView === 'details' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Back Header */}
                            <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid rgba(226,232,240,0.8)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Button 
                                        startIcon={<ChevronLeftIcon />} 
                                        onClick={() => setSurveyTabView('list')}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Back to List
                                    </Button>
                                    <Divider orientation="vertical" flexItem />
                                    <Typography variant="h6" fontWeight={700}>
                                        {surveyForms.find(f => f.form_id === selectedFormId)?.form_name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button 
                                        variant="outlined"
                                        size="small"
                                        startIcon={<FileDownloadIcon />}
                                        onClick={handleDownloadSurveyData}
                                        sx={{ 
                                            borderRadius: '8px', 
                                            textTransform: 'none',
                                            borderColor: 'divider',
                                            color: 'text.secondary',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        Export Excel
                                    </Button>
                                    <Button 
                                        variant={surveyVisualMode === 'table' ? 'contained' : 'outlined'} 
                                        size="small"
                                        startIcon={<TableChartIcon />}
                                        onClick={() => setSurveyVisualMode('table')}
                                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                                    >
                                        Data View
                                    </Button>
                                    <Button 
                                        variant={surveyVisualMode === 'graph' ? 'contained' : 'outlined'} 
                                        size="small"
                                        startIcon={<BarChartIcon />}
                                        onClick={() => setSurveyVisualMode('graph')}
                                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                                    >
                                        Graphical View
                                    </Button>
                                </Box>
                            </Paper>

                            {/* Response Data View */}
                            {surveyVisualMode === 'table' && (
                                <Paper sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(226,232,240,0.8)' }}>
                                    <TableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                                    {surveyResponses.length > 0 && Object.keys(surveyResponses[0].survey_data || {}).map(q => (
                                                        <TableCell key={q} sx={{ fontWeight: 700, minWidth: 150 }}>{q}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {surveyResponses.map(row => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(row.created_at).toLocaleDateString()}</TableCell>
                                                        {Object.keys(row.survey_data || {}).map((q, idx) => (
                                                            <TableCell key={idx}>{row.survey_data[q]}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            )}

                            {/* Graphical Chart View */}
                            {surveyVisualMode === 'graph' && (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: 3 }}>
                                    {(() => {
                                        const res = surveyResponses;
                                        if (!res || res.length === 0) return null;
                                        const allQuestions = Object.keys(res[0].survey_data || {});
                                        const questions = allQuestions.filter(q => {
                                            const lower = q.toLowerCase();
                                            return !lower.includes('name') && 
                                                   !lower.includes('email') && 
                                                   !lower.includes('number') && 
                                                   !lower.includes('id');
                                        });
                                        
                                        return questions.map(q => {
                                            const counts = {};
                                            res.forEach(r => {
                                                const ans = r.survey_data[q]?.toString() || 'No answer';
                                                counts[ans] = (counts[ans] || 0) + 1;
                                            });
                                            
                                            const labels = Object.keys(counts);
                                            const dataValues = Object.values(counts);
                                            
                                            const colors = [
                                                'rgba(37, 99, 235, 0.7)',
                                                'rgba(16, 185, 129, 0.7)',
                                                'rgba(245, 158, 11, 0.7)',
                                                'rgba(239, 68, 68, 0.7)',
                                                'rgba(139, 92, 246, 0.7)',
                                                'rgba(236, 72, 153, 0.7)',
                                                'rgba(107, 114, 128, 0.7)'
                                            ];

                                            const chartData = {
                                                labels: labels,
                                                datasets: [{
                                                    label: 'Responses',
                                                    data: dataValues,
                                                    backgroundColor: colors,
                                                    borderWidth: 1
                                                }]
                                            };

                                            const isMultipleAnswers = labels.length > 2;

                                            const namesByAnswer = {};
                                            res.forEach(r => {
                                                const ans = r.survey_data[q]?.toString() || 'No answer';
                                                // Try to get name from m_name or common keys in survey_data
                                                const person = r.survey_data['Employee Name'] || r.survey_data['Name'] || r.m_name || 'Anonymous';
                                                if (!namesByAnswer[ans]) namesByAnswer[ans] = [];
                                                namesByAnswer[ans].push(person);
                                            });

                                            return (
                                                <Paper key={q} sx={{ p: 3, borderRadius: '16px', border: '1px solid rgba(226,232,240,0.8)' }}>
                                                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>{q}</Typography>
                                                    <Box sx={{ height: 220, display: 'flex', justifyContent: 'center', mb: 2 }}>
                                                        {isMultipleAnswers ? (
                                                            <Bar 
                                                                data={chartData} 
                                                                options={{ 
                                                                    responsive: true, 
                                                                    maintainAspectRatio: false,
                                                                    plugins: { legend: { display: false } }
                                                                }} 
                                                            />
                                                        ) : (
                                                            <Pie 
                                                                data={chartData} 
                                                                options={{ 
                                                                    responsive: true, 
                                                                    maintainAspectRatio: false 
                                                                }} 
                                                            />
                                                        )}
                                                    </Box>
                                                    
                                                    {/* Respondent Details List */}
                                                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(226,232,240,0.5)' }}>
                                                        {labels.map((ans, idx) => (
                                                            <Box key={ans} sx={{ mb: 1 }}>
                                                                <Typography variant="caption" fontWeight={700} color="primary">
                                                                    {ans}:
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                                    {namesByAnswer[ans].map((name, nIdx) => (
                                                                        <Chip 
                                                                            key={nIdx} 
                                                                            label={name} 
                                                                            size="small" 
                                                                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'rgba(226,232,240,0.4)' }} 
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Paper>
                                            );
                                        });
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* Move Department Dialog */}
            <Dialog open={openMoveDialog} onClose={() => setOpenMoveDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SwapIcon color="primary" />
                    Change Department
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Change department assignment for <strong>{selectedUser?.name}</strong>.
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel>Department</InputLabel>
                        <Select
                            value={targetDept}
                            label="Department"
                            onChange={(e) => setTargetDept(e.target.value)}
                        >
                            {departments.map((dept) => (
                                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Or enter new department"
                        size="small"
                        value={targetDept}
                        onChange={(e) => setTargetDept(e.target.value)}
                        sx={{ mt: 2 }}
                        placeholder="Type a new department name"
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenMoveDialog(false)} sx={{ borderRadius: '8px', color: 'text.secondary' }}>Cancel</Button>
                    <Button variant="contained" onClick={handleMoveConfirm} sx={{ borderRadius: '8px' }}>Confirm</Button>
                </DialogActions>
            </Dialog>

            {/* Add Member Dialog */}
            <Dialog open={openAddMemberDialog} onClose={() => setOpenAddMemberDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAddIcon color="primary" />
                    Add New Member
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
                        {/* Full Name */}
                        <TextField
                            fullWidth
                            label="Full Name"
                            size="small"
                            required
                            value={newMember.name}
                            onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                        />
                        {/* Email */}
                        <TextField
                            fullWidth
                            label="Email"
                            size="small"
                            type="email"
                            required
                            value={newMember.email}
                            onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                        />
                        {/* Mobile Number */}
                        <TextField
                            fullWidth
                            label="Mobile Number"
                            size="small"
                            value={newMember.mobile_number}
                            onChange={(e) => setNewMember({ ...newMember, mobile_number: e.target.value })}
                        />
                        {/* Gender */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Gender</InputLabel>
                            <Select
                                value={newMember.gender}
                                label="Gender"
                                onChange={(e) => setNewMember({ ...newMember, gender: e.target.value })}
                            >
                                <MenuItem value="">Not specified</MenuItem>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                                <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Company */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Company</InputLabel>
                            <Select
                                value={newMember.company}
                                label="Company"
                                onChange={(e) => setNewMember({ ...newMember, company: e.target.value })}
                            >
                                <MenuItem value="">Not assigned</MenuItem>
                                {companies.map((comp) => (
                                    <MenuItem key={comp.company} value={comp.company}>{comp.company}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {/* Role */}
                        <TextField
                            fullWidth
                            label="Role"
                            size="small"
                            value={newMember.role}
                            onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                            placeholder="e.g. admin, user, operator"
                        />

                        {/* Department Selection */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Department</InputLabel>
                                <Select
                                    value={newMember.department}
                                    label="Department"
                                    onChange={(e) => setNewMember({ ...newMember, department: e.target.value })}
                                >
                                    <MenuItem value=""><em>Select Department</em></MenuItem>
                                    {departments.map((dept) => (
                                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                fullWidth
                                label="Or enter new department"
                                size="small"
                                value={newMember.department}
                                onChange={(e) => setNewMember({ ...newMember, department: e.target.value })}
                                placeholder="Type a new department name"
                            />
                        </Box>

                        {/* Specialization & Work Details */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Specialization"
                                size="small"
                                value={newMember.specialization}
                                onChange={(e) => setNewMember({ ...newMember, specialization: e.target.value })}
                                placeholder="e.g. Hydraulics, Electrical"
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Certification</InputLabel>
                                    <Select
                                        value={newMember.certification_level}
                                        label="Certification"
                                        onChange={(e) => setNewMember({ ...newMember, certification_level: e.target.value })}
                                    >
                                        <MenuItem value="junior">Junior</MenuItem>
                                        <MenuItem value="senior">Senior</MenuItem>
                                        <MenuItem value="lead">Lead</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    fullWidth
                                    label="Hourly Rate (₹)"
                                    size="small"
                                    type="number"
                                    value={newMember.hourly_rate}
                                    onChange={(e) => setNewMember({ ...newMember, hourly_rate: parseFloat(e.target.value) || 0 })}
                                    InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                                />
                            </Box>
                        </Box>

                        {/* Additional Fields: ID, Password, Accessables */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                                fullWidth
                                label="User ID Code (Optional)"
                                size="small"
                                value={newMember.user_id_code}
                                onChange={(e) => setNewMember({ ...newMember, user_id_code: e.target.value })}
                                placeholder="Auto-generated if empty"
                            />
                            <TextField
                                fullWidth
                                label="Password"
                                size="small"
                                type="text"
                                value={newMember.password}
                                onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                                placeholder="Default: default123"
                            />
                        </Box>
                        <TextField
                            fullWidth
                            label="Accessables / Permissions"
                            size="small"
                            multiline
                            rows={2}
                            value={newMember.accessables}
                            onChange={(e) => setNewMember({ ...newMember, accessables: e.target.value })}
                            placeholder="Specify module permissions or JSON"
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenAddMemberDialog(false)} sx={{ borderRadius: '8px', color: 'text.secondary' }}>Cancel</Button>
                    {isSuperAdmin && (
                        <Button variant="contained" onClick={handleAddMember} sx={{ borderRadius: '8px' }}>Add Member</Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Edit Member Dialog */}
            <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isSuperAdmin ? <EditIcon color="primary" /> : <AssignmentIcon color="primary" />}
                    {isSuperAdmin ? 'Edit Member Details' : 'Member Details'}
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
                        <TextField
                            fullWidth
                            label="User ID Code"
                            size="small"
                            value={editMember.user_id_code || ''}
                            onChange={(e) => setEditMember({ ...editMember, user_id_code: e.target.value })}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        <TextField
                            fullWidth
                            label="New Password"
                            size="small"
                            type="text"
                            value={editMember.password || ''}
                            onChange={(e) => setEditMember({ ...editMember, password: e.target.value })}
                            placeholder={isSuperAdmin ? "Leave blank to keep current" : "Confidential"}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        <TextField
                            fullWidth
                            label="Accessables / Permissions"
                            size="small"
                            multiline
                            rows={2}
                            value={editMember.accessables || ''}
                            onChange={(e) => setEditMember({ ...editMember, accessables: e.target.value })}
                            sx={{ gridColumn: 'span 2' }}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        {/* Full Name */}
                        <TextField
                            fullWidth
                            label="Full Name"
                            size="small"
                            value={editMember.name || ''}
                            onChange={(e) => setEditMember({ ...editMember, name: e.target.value })}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        {/* Email */}
                        <TextField
                            fullWidth
                            label="Email"
                            size="small"
                            type="email"
                            value={editMember.email || ''}
                            onChange={(e) => setEditMember({ ...editMember, email: e.target.value })}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        {/* Mobile Number */}
                        <TextField
                            fullWidth
                            label="Mobile Number"
                            size="small"
                            value={editMember.mobile_number || ''}
                            onChange={(e) => setEditMember({ ...editMember, mobile_number: e.target.value })}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        {/* Gender */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Gender</InputLabel>
                            <Select
                                value={editMember.gender || ''}
                                label="Gender"
                                onChange={(e) => setEditMember({ ...editMember, gender: e.target.value })}
                                disabled={!isSuperAdmin}
                            >
                                <MenuItem value="">Not specified</MenuItem>
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                                <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Department */}
                        <TextField
                            fullWidth
                            label="Department"
                            size="small"
                            value={editMember.department || ''}
                            onChange={(e) => setEditMember({ ...editMember, department: e.target.value })}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        {/* Company */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Company</InputLabel>
                            <Select
                                value={editMember.company || ''}
                                label="Company"
                                onChange={(e) => setEditMember({ ...editMember, company: e.target.value })}
                                disabled={!isSuperAdmin}
                            >
                                <MenuItem value="">Not assigned</MenuItem>
                                {companies.map((comp) => (
                                    <MenuItem key={comp.company} value={comp.company}>{comp.company}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {/* Role */}
                        <TextField
                            fullWidth
                            label="Role"
                            size="small"
                            value={editMember.role || ''}
                            onChange={(e) => setEditMember({ ...editMember, role: e.target.value })}
                            placeholder="e.g. admin, user, operator"
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        {/* Specialization */}
                        <TextField
                            fullWidth
                            label="Specialization"
                            size="small"
                            value={editMember.specialization || ''}
                            onChange={(e) => setEditMember({ ...editMember, specialization: e.target.value })}
                            InputProps={{ readOnly: !isSuperAdmin }}
                        />
                        {/* Certification Level */}
                        <FormControl fullWidth size="small">
                            <InputLabel>Certification Level</InputLabel>
                            <Select
                                value={editMember.certification_level || 'junior'}
                                label="Certification Level"
                                onChange={(e) => setEditMember({ ...editMember, certification_level: e.target.value })}
                                disabled={!isSuperAdmin}
                            >
                                <MenuItem value="junior">Junior</MenuItem>
                                <MenuItem value="senior">Senior</MenuItem>
                                <MenuItem value="lead">Lead</MenuItem>
                            </Select>
                        </FormControl>
                        {/* Hourly Rate */}
                        <TextField
                            fullWidth
                            label="Hourly Rate (₹)"
                            size="small"
                            type="number"
                            value={editMember.hourly_rate || 0}
                            onChange={(e) => setEditMember({ ...editMember, hourly_rate: parseFloat(e.target.value) || 0 })}
                            InputProps={{ 
                                inputProps: { min: 0, step: 0.01 },
                                readOnly: !isSuperAdmin 
                            }}
                        />
                        {/* Created At - Read Only */}
                        <TextField
                            fullWidth
                            label="Created At"
                            size="small"
                            value={editMember.created_at ? new Date(editMember.created_at).toLocaleString() : 'N/A'}
                            InputProps={{ readOnly: true }}
                            sx={{ bgcolor: 'action.hover' }}
                        />
                        {/* Last Login - Read Only */}
                        <TextField
                            fullWidth
                            label="Last Login"
                            size="small"
                            value={editMember.last_login ? new Date(editMember.last_login).toLocaleString() : 'Never logged in'}
                            InputProps={{ readOnly: true }}
                            sx={{ bgcolor: 'action.hover' }}
                        />
                    </Box>

                    {/* Email Configuration Section */}
                    <Box sx={{ mt: 4, mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <RefreshIcon sx={{ fontSize: 20 }} />
                            Email Configuration
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <TextField
                                fullWidth
                                label="Configured Email Address"
                                size="small"
                                value={editMember.config_email || ''}
                                onChange={(e) => setEditMember({ ...editMember, config_email: e.target.value })}
                                helperText={isSuperAdmin ? "Email used for SMTP/IMAP authentication" : ""}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />
                            <Box sx={{ display: 'none' }} /> {/* Spacer */}

                            <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1', mt: 1, color: 'text.secondary' }}>SMTP Settings (Sending)</Typography>
                            <TextField
                                fullWidth
                                label="SMTP Host"
                                size="small"
                                value={editMember.smtp_host || ''}
                                onChange={(e) => setEditMember({ ...editMember, smtp_host: e.target.value })}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />
                            <TextField
                                fullWidth
                                label="SMTP Port"
                                size="small"
                                type="number"
                                value={editMember.smtp_port || ''}
                                onChange={(e) => setEditMember({ ...editMember, smtp_port: parseInt(e.target.value) || 0 })}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />
                            <TextField
                                fullWidth
                                label="SMTP User"
                                size="small"
                                value={editMember.smtp_user || ''}
                                onChange={(e) => setEditMember({ ...editMember, smtp_user: e.target.value })}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />
                            <TextField
                                fullWidth
                                label="SMTP Password"
                                size="small"
                                type="text"
                                placeholder={isSuperAdmin ? "Leave blank to keep current" : "Confidential"}
                                value={editMember.smtp_pass || ''}
                                onChange={(e) => setEditMember({ ...editMember, smtp_pass: e.target.value })}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />

                            <Typography variant="subtitle2" sx={{ gridColumn: '1 / -1', mt: 2, color: 'text.secondary' }}>IMAP Settings (Receiving)</Typography>
                            <TextField
                                fullWidth
                                label="IMAP Host"
                                size="small"
                                value={editMember.imap_host || ''}
                                onChange={(e) => setEditMember({ ...editMember, imap_host: e.target.value })}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />
                            <TextField
                                fullWidth
                                label="IMAP Port"
                                size="small"
                                type="number"
                                value={editMember.imap_port || ''}
                                onChange={(e) => setEditMember({ ...editMember, imap_port: parseInt(e.target.value) || 0 })}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />
                            <TextField
                                fullWidth
                                label="IMAP User"
                                size="small"
                                value={editMember.imap_user || ''}
                                onChange={(e) => setEditMember({ ...editMember, imap_user: e.target.value })}
                                InputProps={{ readOnly: !isSuperAdmin }}
                            />
                            <TextField
                                fullWidth
                                label="IMAP Password"
                                size="small"
                                type="text"
                                placeholder="Leave blank to keep current"
                                value={editMember.imap_pass || ''}
                                onChange={(e) => setEditMember({ ...editMember, imap_pass: e.target.value })}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenEditDialog(false)} sx={{ borderRadius: '8px', color: 'text.secondary' }}>Close</Button>
                    {isSuperAdmin && (
                        <Button variant="contained" onClick={handleEditConfirm} sx={{ borderRadius: '8px' }}>Save Changes</Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ color: 'error.main' }}>
                    Delete Member
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDeleteDialog(false)} sx={{ borderRadius: '8px' }}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ borderRadius: '8px' }}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* Action Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                        mt: 1.5,
                        borderRadius: '12px',
                        minWidth: 150
                    },
                }}
            >
                <MenuItem onClick={handleEditClick} sx={{ py: 1.5 }}>
                    <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                    Edit Details
                </MenuItem>
                <MenuItem onClick={handleMoveClick} sx={{ py: 1.5 }}>
                    <ListItemIcon><SwapIcon fontSize="small" /></ListItemIcon>
                    Change Department
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleDeleteClick} sx={{ py: 1.5, color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    Remove Member
                </MenuItem>
            </Menu>

            {/* Department Menu */}
            <Menu
                anchorEl={deptAnchorEl}
                open={Boolean(deptAnchorEl)}
                onClose={handleDeptMenuClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
                        mt: 1.5,
                        borderRadius: '12px',
                        minWidth: 120
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        setSelectedDept(selectedDeptForMenu);
                        setActiveTab(0);
                        handleDeptMenuClose();
                    }}
                    sx={{ py: 1 }}
                >
                    <ListItemIcon><GroupIcon fontSize="small" /></ListItemIcon>
                    View Members
                </MenuItem>
            </Menu>

             {/* Survey Connection Guide Dialog */}
            <Dialog open={openSurveyGuide} onClose={() => setOpenSurveyGuide(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentIcon color="primary" />
                    How to Store Google Form Responses
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Step 1: Create your Google Form</Typography>
                    <Typography variant="body2" paragraph>
                        Create your survey on Google Forms. Make sure to include any questions you want to track.
                    </Typography>

                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Step 2: Add the Sync Script</Typography>
                    <Typography variant="body2" paragraph>
                        In your Google Form, click on <strong>Extensions &gt; Apps Script</strong>. Delete any existing code and paste the following:
                    </Typography>
                    
                    <Box sx={{ 
                        bgcolor: '#1e293b', 
                        color: '#f8fafc', 
                        p: 2, 
                        borderRadius: '8px', 
                        fontFamily: 'monospace', 
                        fontSize: '0.8rem',
                        overflowX: 'auto',
                        mb: 3
                    }}>
                        <pre style={{ margin: 0 }}>{`function onFormSubmit(e) {
  var form = FormApp.getActiveForm();
  var responses = e.response;
  var itemResponses = responses.getItemResponses();
  var surveyData = {};
  
  for (var i = 0; i < itemResponses.length; i++) {
    var itemResponse = itemResponses[i];
    surveyData[itemResponse.getItem().getTitle()] = itemResponse.getResponse();
  }
  
  var payload = {
    form_id: form.getId(),
    form_name: form.getTitle(),
    employee_email: responses.getRespondentEmail(),
    survey_data: surveyData,
    m_name: "Google Forms User"
  };
  
  var options = {
    'method' : 'post',
    'contentType': 'application/json',
    'payload' : JSON.stringify(payload)
  };
  
  // Replace with your actual server URL
  var serverUrl = "https://api.mtandtiot.in/api/surveys/submit";
  UrlFetchApp.fetch(serverUrl, options);
}`}</pre>
                    </Box>

                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>Step 3: Set up the Trigger</Typography>
                    <Typography variant="body2" component="div">
                        <ol>
                            <li>In the Apps Script editor, click on the <strong>Triggers</strong> (clock icon) in the left sidebar.</li>
                            <li>Click <strong>+ Add Trigger</strong>.</li>
                            <li>Choose <code>onFormSubmit</code> as the function to run.</li>
                            <li>Select <strong>From form</strong> as the event source.</li>
                            <li>Select <strong>On form submit</strong> as the event type.</li>
                            <li>Click Save and approve the permissions.</li>
                        </ol>
                    </Typography>
                    <Alert severity="info" sx={{ mt: 2 }}>
                        Once set up, every new response in your Google Form will automatically appear in this dashboard.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenSurveyGuide(false)} variant="contained" sx={{ borderRadius: '8px' }}>
                        Got it!
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Operator details — read-only (operators have no accounts) */}
            <Dialog open={Boolean(operatorDetail)} onClose={() => setOperatorDetail(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>
                    {operatorDetail?.name}
                    <Typography variant="caption" display="block" color="text.secondary">
                        {operatorDetail?.user_id_code} · Operator · Operations
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {operatorDetail && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 1.25, columnGap: 1, fontSize: '0.875rem' }}>
                            {[
                                ['Emp ID', operatorDetail.user_id_code],
                                ['Region', operatorDetail.region],
                                ['Mobile', operatorDetail.mobile_number],
                                ['E-Mail', operatorDetail.email],
                                ['Emergency Contact', operatorDetail.emergency_contact],
                                ["Father's Name", operatorDetail.father_name],
                                ['Date of Birth', operatorDetail.date_of_birth
                                    ? new Date(operatorDetail.date_of_birth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null],
                                ['Date of Joining', operatorDetail.date_of_joining
                                    ? new Date(operatorDetail.date_of_joining).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null],
                            ].map(([label, value]) => (
                                <React.Fragment key={label}>
                                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                                    <Typography variant="body2" fontWeight={500}>{value || '—'}</Typography>
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                    <Alert severity="info" sx={{ mt: 2, borderRadius: '8px' }}>
                        Field operator — no website account or login.
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOperatorDetail(null)} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default OrganizationPage;
